import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageTitle from '../../components/common/PageTitle';
import ProductCard from '../../components/cards/ProductCard';
import SectionHeader from '../../components/common/SectionHeader';
import { useDispatch, useSelector } from 'react-redux';
import { getOtherProducts, getSingleData } from '../../services/actions/MainAction';
import { 
  Zap, 
  Shield, 
  CheckCircle, 
  ArrowRight,
  Sparkles,
  Package,
  Battery,
  Clock,
  Award,
  Share2,
  Eye,
  ShoppingCart,
  ChevronLeft,
  ChevronRight,
  Star,
  Tag,
  Truck,
  RotateCcw
} from 'lucide-react';

function SingleProduct() {
  const contentData = useSelector(state => state.Data.contentData);
  const otherProducts = useSelector(state => state.Data.otherProducts);
  const lang_id = useSelector(state => state.Data.lang_id);
  const [singleData, setSingleData] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const params = useParams();
  const dispatch = useDispatch();

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(getSingleData(`product/${params?.id}?lang_id=${lang_id}`))
      .then(resp => {
        setSingleData(resp);
        if(resp) {
          dispatch(getOtherProducts(lang_id, resp?.id));
        }
      });
  }, [params?.id, lang_id, dispatch]);

  // Collect all available images
  const productImages = [
    singleData?.image_full_url,
    singleData?.image_2_full_url,
    singleData?.image_3_full_url,
    singleData?.image_4_full_url
  ].filter(Boolean);

  // Product features
  const productFeatures = [
    { icon: <Shield className="w-5 h-5" />, text: "Zəmanətli", color: "text-green-600" },
    { icon: <Battery className="w-5 h-5" />, text: "Enerji Qənaətçisi", color: "text-blue-600" },
    { icon: <Clock className="w-5 h-5" />, text: "Uzun Ömür", color: "text-amber-600" },
    { icon: <Award className="w-5 h-5" />, text: "Premium Keyfiyyət", color: "text-purple-600" }
  ];

  // Product specifications
  const specifications = [
    { label: "Marka", value: "Premium" },
    { label: "Güc", value: "220V" },
    { label: "Zəmanət", value: "2 il" },
    { label: "Ölçü", value: "Standart" },
    { label: "Material", value: "Polikarbonat" },
    { label: "Rəng", value: "Ağ" }
  ];

  if (!singleData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Məhsul yüklənir...</p>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 py-16">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 border-2 border-blue-400 rounded-full animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-48 h-48 border-2 border-amber-400 rounded-full animate-pulse"></div>
        </div>

        <div className="relative container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 rounded-full mb-6 border border-blue-400/30 backdrop-blur-sm">
              <Sparkles className="w-4 h-4 text-blue-300" />
              <span className="text-sm font-medium text-blue-100">Məhsul</span>
              <Sparkles className="w-4 h-4 text-amber-300" />
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight">
              <span className="bg-gradient-to-r from-white via-blue-100 to-white bg-clip-text text-transparent">
                {singleData?.translate?.title}
              </span>
            </h1>
            
            <p className="text-lg text-blue-100 max-w-3xl mx-auto leading-relaxed">
              Professional elektrik avadanlığı və peşəkar həllər
            </p>
          </div>
        </div>
      </div>

      {/* Product Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Left Column - Images */}
              <div>
                {/* Main Image */}
                <div className="relative rounded-2xl overflow-hidden shadow-xl mb-6 group cursor-pointer" onClick={() => setImageModalOpen(true)}>
                  <div className="relative h-80 md:h-96 bg-gradient-to-br from-gray-100 to-gray-200">
                    {productImages[currentImageIndex] ? (
                      <img 
                        src={productImages[currentImageIndex]} 
                        alt={`${singleData?.translate?.title} - görüntü ${currentImageIndex + 1}`}
                        className="w-full h-full object-contain p-8"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-24 h-24 text-gray-300" />
                      </div>
                    )}
                    
                    {/* Image Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="bg-white/90 backdrop-blur-sm rounded-full p-4">
                        <Eye className="w-6 h-6 text-gray-800" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Badge */}
                  <div className="absolute top-6 right-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                      <Zap className="w-7 h-7 text-white" />
                    </div>
                  </div>
                </div>

                {/* Thumbnail Gallery */}
                {productImages.length > 1 && (
                  <div className="grid grid-cols-4 gap-3">
                    {productImages.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`relative rounded-xl overflow-hidden border-2 transition-all duration-300 ${
                          currentImageIndex === index 
                            ? 'border-blue-500 shadow-md' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="aspect-square bg-gray-100">
                          {image ? (
                            <img 
                              src={image} 
                              alt={`Thumbnail ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                        </div>
                        
                        {/* Selected Indicator */}
                        {currentImageIndex === index && (
                          <div className="absolute inset-0 bg-blue-500/20"></div>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {/* Navigation Buttons */}
                {productImages.length > 1 && (
                  <div className="flex items-center justify-center gap-4 mt-6">
                    <button
                      onClick={() => setCurrentImageIndex(prev => prev > 0 ? prev - 1 : productImages.length - 1)}
                      className="w-10 h-10 bg-white border border-gray-300 rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors shadow-sm"
                    >
                      <ChevronLeft className="w-5 h-5 text-gray-700" />
                    </button>
                    <div className="text-sm text-gray-600">
                      {currentImageIndex + 1} / {productImages.length}
                    </div>
                    <button
                      onClick={() => setCurrentImageIndex(prev => prev < productImages.length - 1 ? prev + 1 : 0)}
                      className="w-10 h-10 bg-white border border-gray-300 rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors shadow-sm"
                    >
                      <ChevronRight className="w-5 h-5 text-gray-700" />
                    </button>
                  </div>
                )}
              </div>

              {/* Right Column - Product Info */}
              <div>
                <div className="sticky top-24 space-y-8">
                  {/* Product Header */}
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full">
                        Elektrik Avadanlığı
                      </div>
                    </div>
                    
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                      {singleData?.translate?.title}
                    </h2>
                    
                   
                  </div>

                  {/* Product Features */}
                  <div className="grid grid-cols-2 gap-3">
                    {productFeatures.map((feature, index) => (
                      <div key={index} className="flex items-center gap-3 bg-white p-3 rounded-xl border border-gray-200">
                        <div className={`${feature.color}`}>
                          {feature.icon}
                        </div>
                        <span className="text-sm font-medium text-gray-900">{feature.text}</span>
                      </div>
                    ))}
                  </div>


                </div>
              </div>
            </div>

            {/* Product Description */}
            <div className="mt-16">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Məhsul Haqqında</h2>
                    <p className="text-gray-600">Ətraflı məlumat və xüsusiyyətlər</p>
                  </div>
                </div>
                
                <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                  <div dangerouslySetInnerHTML={{__html: singleData?.translate?.description}} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Products */}
      <section className="py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full mb-4 border border-blue-100">
                <Sparkles className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-medium text-blue-700">Digər Məhsullar</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {contentData?.other_prod || "Oxşar Məhsullar"}
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Eyni kateqoriyadan digər keyfiyyətli elektrik məhsulları
              </p>
              
              <div className="h-1 w-24 bg-gradient-to-r from-blue-500 to-amber-500 mx-auto mt-6 rounded-full"></div>
            </div>

            {/* Other Products Grid */}
            {otherProducts?.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {otherProducts.map((data, i) => (
                  <ProductCard key={i} data={data} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                Hal-hazırda oxşar məhsullar mövcud deyil
              </div>
            )}

            {/* View All Button */}
            <div className="text-center mt-12">
              <Link
                to="/products"
                className="inline-flex items-center gap-3 px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <span>Bütün Məhsullar</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Image Modal */}
      {imageModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="relative max-w-5xl max-h-[90vh]">
            <button
              onClick={() => setImageModalOpen(false)}
              className="absolute -top-12 right-0 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors z-50"
            >
              <span className="text-white text-xl">×</span>
            </button>
            
            <div className="rounded-lg overflow-hidden">
              <img 
                src={productImages[currentImageIndex]} 
                alt={singleData?.translate?.title}
                className="w-full h-auto max-h-[80vh] object-contain"
              />
            </div>
          </div>
          
          <div 
            className="absolute inset-0 -z-10"
            onClick={() => setImageModalOpen(false)}
          />
        </div>
      )}
    </main>
  );
}

export default SingleProduct;